/**
 * Competition Analytics Dashboard Page
 * TBMuktBharat 2026 - Government Grade UI
 */

import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Trophy, Users, FileText, CheckCircle, XCircle, Clock,
  TrendingUp, MapPin, Calendar, BarChart2, PieChart
} from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import competitionService, { CompetitionAnalytics, TypeStat, GeographicStat, MonthlyStat } from '@/services/competitionService'
import { Card, Badge, EmptyState } from '@/components/ui/premium-components'
import { StatsCard, AnimatedCounter } from '@/components/ui/data-display'
import GovLayout from '@/components/layout/GovLayout'

// Simple Bar Chart Component
const BarChart: React.FC<{
  data: { label: string; value: number }[]
  title: string
  color?: string
}> = ({ data, title, color = 'bg-primary-500' }) => {
  const maxValue = Math.max(...data.map(d => d.value), 1)

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-xl p-6 border border-neutral-200 dark:border-neutral-800">
      <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">{title}</h3>
      <div className="space-y-3">
        {data.map((item, index) => (
          <div key={index} className="flex items-center gap-3">
            <div className="w-24 text-sm text-neutral-600 dark:text-neutral-400 truncate">{item.label}</div>
            <div className="flex-1 h-6 bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(item.value / maxValue) * 100}%` }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`h-full ${color} rounded-full`}
              />
            </div>
            <div className="w-12 text-sm font-medium text-neutral-900 dark:text-white text-right">
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Geographic Map Placeholder (would need a real map library for production)
const GeographicChart: React.FC<{
  data: GeographicStat[]
  title: string
}> = ({ data, title }) => {
  return (
    <div className="bg-white dark:bg-neutral-900 rounded-xl p-6 border border-neutral-200 dark:border-neutral-800">
      <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">{title}</h3>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {data.slice(0, 10).map((item, index) => (
          <div key={index} className="flex items-center justify-between py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-neutral-400" />
              <span className="text-sm text-neutral-700 dark:text-neutral-300">
                {item.district ? `${item.district}, ` : ''}{item.state}
              </span>
            </div>
            <Badge variant="primary" size="sm">{item.count}</Badge>
          </div>
        ))}
      </div>
    </div>
  )
}

// Line Chart for Trends
const LineChart: React.FC<{
  data: MonthlyStat[]
  title: string
}> = ({ data, title }) => {
  const maxValue = Math.max(...data.map(d => d.submissions), 1)

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-xl p-6 border border-neutral-200 dark:border-neutral-800">
      <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">{title}</h3>
      <div className="flex items-end gap-1 h-40">
        {data.slice(-12).map((item, index) => (
          <motion.div
            key={index}
            initial={{ height: 0 }}
            animate={{ height: `${(item.submissions / maxValue) * 100}%` }}
            transition={{ duration: 0.5, delay: index * 0.05 }}
            className="flex-1 bg-gradient-to-t from-primary-500 to-primary-400 rounded-t-sm min-h-[4px] relative group"
          >
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              {item.submissions}
            </div>
          </motion.div>
        ))}
      </div>
      <div className="flex justify-between mt-2 text-xs text-neutral-500">
        {data.slice(-12).map((item, index) => (
          <span key={index} className="flex-1 text-center truncate">
            {item.month.split('-')[1]}
          </span>
        ))}
      </div>
    </div>
  )
}

export const CompetitionAnalyticsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [analytics, setAnalytics] = useState<CompetitionAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchAnalytics()
  }, [id])

  const fetchAnalytics = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = id
        ? await competitionService.getCompetitionAnalytics(parseInt(id))
        : await competitionService.getAnalytics()
      setAnalytics(data)
    } catch (err) {
      console.error('Failed to fetch analytics:', err)
      setError('Failed to load analytics. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <GovLayout>
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-neutral-200 dark:bg-neutral-800 rounded w-1/4" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-neutral-200 dark:bg-neutral-800 rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </GovLayout>
    )
  }

  if (error) {
    return (
      <GovLayout>
        <div className="max-w-7xl mx-auto">
          <EmptyState
            icon={<BarChart2 className="w-16 h-16 text-neutral-300" />}
            title="Error Loading Analytics"
            description={error}
          />
        </div>
      </GovLayout>
    )
  }

  if (!analytics) {
    return (
      <GovLayout>
        <div className="max-w-7xl mx-auto">
          <EmptyState
            icon={<BarChart2 className="w-16 h-16 text-neutral-300" />}
            title="No Analytics Data"
            description="Start by creating competitions and receiving submissions."
          />
        </div>
      </GovLayout>
    )
  }

  return (
    <GovLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">
            {id ? 'Competition Analytics' : 'Analytics Dashboard'}
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400 mt-1">
            {id ? 'Detailed statistics for this competition' : 'Overall competition statistics and trends'}
          </p>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          <StatsCard
            icon={<Trophy className="w-6 h-6 text-primary-600" />}
            label="Total Competitions"
            value={analytics.totalCompetitions}
            iconBgColor="bg-primary-100"
          />
          <StatsCard
            icon={<CheckCircle className="w-6 h-6 text-green-600" />}
            label="Active Competitions"
            value={analytics.activeCompetitions}
            iconBgColor="bg-green-100"
          />
          <StatsCard
            icon={<FileText className="w-6 h-6 text-blue-600" />}
            label="Total Submissions"
            value={analytics.totalSubmissions}
            iconBgColor="bg-blue-100"
          />
          <StatsCard
            icon={<Clock className="w-6 h-6 text-orange-600" />}
            label="Pending Reviews"
            value={analytics.pendingSubmissions}
            iconBgColor="bg-orange-100"
          />
        </motion.div>

        {/* Submission Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
        >
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500">Approved</p>
                <p className="text-2xl font-bold text-green-600">
                  <AnimatedCounter value={analytics.approvedSubmissions} />
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-200" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500">Pending</p>
                <p className="text-2xl font-bold text-orange-600">
                  <AnimatedCounter value={analytics.pendingSubmissions} />
                </p>
              </div>
              <Clock className="w-10 h-10 text-orange-200" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500">Rejected</p>
                <p className="text-2xl font-bold text-red-600">
                  <AnimatedCounter value={analytics.rejectedSubmissions} />
                </p>
              </div>
              <XCircle className="w-10 h-10 text-red-200" />
            </div>
          </Card>
        </motion.div>

        {/* Charts Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
        >
          {/* Type Distribution */}
          {analytics.typeDistribution && analytics.typeDistribution.length > 0 && (
            <BarChart
              data={analytics.typeDistribution.map(t => ({ label: t.type, value: t.count }))}
              title="Competition Types"
              color="bg-gradient-to-r from-purple-500 to-pink-500"
            />
          )}

          {/* Geographic Distribution */}
          {analytics.stateDistribution && analytics.stateDistribution.length > 0 && (
            <GeographicChart
              data={analytics.stateDistribution}
              title="Submissions by State"
            />
          )}
        </motion.div>

        {/* District Distribution */}
        {analytics.districtDistribution && analytics.districtDistribution.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-8"
          >
            <GeographicChart
              data={analytics.districtDistribution}
              title="Submissions by District"
            />
          </motion.div>
        )}

        {/* Trend Charts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          {analytics.monthlyTrend && analytics.monthlyTrend.length > 0 && (
            <LineChart
              data={analytics.monthlyTrend}
              title="Monthly Submissions Trend"
            />
          )}
          {analytics.dailyTrend && analytics.dailyTrend.length > 0 && (
            <LineChart
              data={analytics.dailyTrend}
              title="Daily Submissions Trend"
            />
          )}
        </motion.div>

        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8"
        >
          <button
            onClick={() => navigate(-1)}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Competitions
          </button>
        </motion.div>
      </div>
    </GovLayout>
  )
}

export default CompetitionAnalyticsPage